import React, { FC, useState } from 'react'
import { Flex } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import PhoneInput from 'react-phone-number-input/input'

import { OrganisationRoleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import {
  ButtonClean,
  ButtonDanger,
  ButtonPrimary,
  Switch,
  UserSelect,
  Tooltip,
  Modal
} from 'front-end/src/components'

import { availableRoles, isOrgEmailEnabled } from './utils'
import { UserPasswordReset } from './UserPasswordReset'
import { UserCreationInfo } from './UserCreationInfo'
import IconTick from '../../components/common/icon/IconTick'
import IconQuestion from '../../components/common/icon/IconQuestion'

import Message, { MessageTypes } from '../../components/common/message/Message'
import { LanguageDropdown } from '../../components/common/language-dropdown/LanguageDropdown'
import Select from '../../components/common/select/Select'
import SelectTimezone from '../../components/common/SelectTimezone'
import { UserImage } from './UserImage'

import { staticLinks } from 'shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { notification } from '../../components/common/notifications/Notifications'
import { usePatchUserPreferencesMutation } from './common.codegen'
import { LogInAsUser } from './LogInAsUser'
import {
  useAmIOrganisationOwnerQuery,
  useArchiveUserMutation
} from './UserBasicInfo.codegen'
import { UserIsActiveSwitch } from './UserIsActiveSwitch'
import {
  useUserProfilePageDetailAsMeQuery,
  useUserProfilePageDetailAsAdminQuery,
  UserProfilePageDetailAsMeFragment
} from './UserProfilePage.codegen'
import { UserVerification } from 'front-end/src/pages/user/UserVerification'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import { useOrganisation } from 'front-end/src/public-api/hooks/useOrganisation'
import { isEqual } from 'lodash'

const FormRow = ({ children, dataTest = 'FormRow', ...props }) => {
  return (
    <div className="form-row" data-test={dataTest} {...props}>
      {children}
    </div>
  )
}
// TODO refactor with formik and yup for validation
const UserBasicInfo: FC<{
  initialUserData: UserProfilePageDetailAsMeFragment
  user: UserProfilePageDetailAsMeFragment
  handleUserForm
  isCurrentUser
  submitUserForm
  canEdit
}> = ({
  initialUserData,
  user,
  handleUserForm,
  isCurrentUser,
  submitUserForm,
  canEdit
}) => {
  const { isAdmin } = usePublicApiSession()
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [archiveUser, { loading }] = useArchiveUserMutation()
  const { featureFlags } = useFeatureFlags()

  const { refetch } = isCurrentUser
    ? useUserProfilePageDetailAsMeQuery()
    : useUserProfilePageDetailAsAdminQuery({
        variables: {
          userId: user.id
        }
      })

  const [patchUserPreference, { loading: loadingUserPreferences }] =
    usePatchUserPreferencesMutation()

  const { data } = useAmIOrganisationOwnerQuery()
  const { data: orgData } = useOrganisation()

  const isAdminEditing = !isCurrentUser && canEdit

  const showScimImmutableResourceNotif = () => {
    if (user.scimResource) {
      notification.warning(t`SCIM user fields cannot be modified in Looop`)
    }
  }

  return (
    <div className="User__basic">
      {!user.deleted_at && !user.verified && isCurrentUser && (
        <UserVerification user={user} />
      )}

      {user.deleted_at && (
        <Message type={MessageTypes.WARNING}>
          <Trans>This user was archived</Trans>
        </Message>
      )}
      <Flex justifyContent="center" flexDirection={['column', 'row']}>
        <UserImage
          isCurrentUser={isCurrentUser}
          user={user}
          handleUserForm={handleUserForm}
          canEdit={canEdit}
        />
      </Flex>

      <FormRow>
        <label htmlFor="display_name" className="form-label">
          <Trans>Display name</Trans>
        </label>
        <input
          id="display_name"
          type="text"
          className="form-textInput"
          defaultValue={user.display_name as string}
          disabled={!canEdit}
          onChange={(e) =>
            handleUserForm({
              display_name: e.currentTarget.value
            })
          }
        />
      </FormRow>
      <FormRow>
        <label htmlFor="first_name" className="form-label">
          <Trans>First name</Trans>
        </label>
        <input
          id="first_name"
          type="text"
          defaultValue={user.first_name as string}
          autoComplete="given-name"
          className="form-textInput"
          disabled={!canEdit}
          onChange={(e) =>
            handleUserForm({
              first_name: e.currentTarget.value
            })
          }
        />
      </FormRow>
      <FormRow>
        <label htmlFor="last_name" className="form-label">
          <Trans>Last name</Trans>
        </label>
        <input
          id="last_name"
          type="text"
          autoComplete="family-name"
          defaultValue={user.last_name as string}
          className="form-textInput"
          disabled={!canEdit}
          onChange={(e) =>
            handleUserForm({
              last_name: e.currentTarget.value
            })
          }
        />
      </FormRow>
      <FormRow onClick={showScimImmutableResourceNotif}>
        <label htmlFor="email" className="form-label">
          <Trans>Email</Trans>
          {` `}
          {user.verified ? (
            <Tooltip text="Verified">
              <IconTick />
            </Tooltip>
          ) : (
            <Tooltip text="Not verified">
              <IconTick color="grey" />
            </Tooltip>
          )}
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          defaultValue={user.email as string}
          disabled={!canEdit || !!user.scimResource}
          className={`form-textInput ${
            user.verified ? 'email-verified' : 'email-unverified'
          }`}
          onChange={(e) =>
            handleUserForm({
              email: e.currentTarget.value
            })
          }
        />
      </FormRow>
      <FormRow>
        <label htmlFor="phone" className="form-label">
          <Trans>
            Mobile number (for sending SMS) including country calling code, e.g.
            UK is +447551234567
          </Trans>
        </label>
        <PhoneInput
          id="phone"
          className="form-textInput"
          value={user.phone_number as string | undefined}
          disabled={!canEdit}
          onChange={(value) => {
            handleUserForm({
              phone_number: value ? value : null
            })
          }}
        />
      </FormRow>
      {featureFlags?.account_name_login && (
        <FormRow onClick={showScimImmutableResourceNotif}>
          <label htmlFor="account_name" className="form-label">
            <Trans>Account name</Trans>
          </label>
          <input
            id="account_name"
            type="text"
            className="form-textInput"
            defaultValue={user.account_name as string}
            disabled={!canEdit || !!user.scimResource}
            onChange={(e) =>
              handleUserForm({
                account_name: e.currentTarget.value
              })
            }
          />
        </FormRow>
      )}
      {orgData.organisation.id && (
        <FormRow onClick={showScimImmutableResourceNotif}>
          <label htmlFor="role" className="form-label">
            <Trans>Role</Trans>
          </label>
          <Select
            inputId="role"
            isDisabled={isCurrentUser || !isAdmin || (isCurrentUser && isAdmin)}
            options={availableRoles}
            value={availableRoles.find(({ value: roleValue }) => {
              return roleValue === user.organisation_role
            })}
            onChange={({ value }: any) => {
              handleUserForm({
                organisation_role: value
              })
            }}
            className="roleSelect"
          />
        </FormRow>
      )}

      <FormRow onClick={showScimImmutableResourceNotif}>
        <label htmlFor="manager_id" className="form-label">
          <Trans>Manager</Trans>
        </label>
        <UserSelect
          inputId="manager_id"
          isDisabled={!isAdminEditing || !!user.scimResource}
          // @ts-expect-error
          value={user.manager}
          onChange={(manager) =>
            handleUserForm({ manager, manager_id: manager.id })
          }
          dataTest="ManagerID"
        />
      </FormRow>

      <FormRow dataTest="TimeZoneSelect">
        <label htmlFor="timezone" className="form-label">
          <Trans>Timezone</Trans>
        </label>
        <SelectTimezone
          value={user.timezone}
          name="timezone"
          inputId="timezone"
          guess
          onChange={(tz) => {
            handleUserForm({ timezone: tz })
          }}
        />
      </FormRow>

      <FormRow dataTest="LanguageSelect">
        <label htmlFor="language" className="form-label">
          <Trans>Language</Trans>
        </label>
        <LanguageDropdown
          inputId="language"
          value={user.locale}
          onChange={(locale) => {
            handleUserForm({ locale })
          }}
        />
      </FormRow>

      <div className="User__submit">
        <ButtonPrimary
          disabled={isEqual(initialUserData, user)}
          onClick={async () => {
            await submitUserForm()
            refetch()
          }}
        >
          <Trans>Update details</Trans>
        </ButtonPrimary>
      </div>

      {isOrgEmailEnabled(orgData.organisation) && isCurrentUser && (
        <div className="User__summaryEmails">
          <h3>
            <Trans>Activity summary emails</Trans>
          </h3>
          <Switch
            isChecked={!!user.preferences.sendCommentSummaryEmails}
            isDisabled={!canEdit || loadingUserPreferences}
            onChange={async () => {
              const { data } = await patchUserPreference({
                variables: {
                  input: {
                    preferences: {
                      sendCommentSummaryEmails:
                        !user.preferences.sendCommentSummaryEmails
                    }
                  }
                }
              })
              if (data && data.me.patch?.preferences) {
                refetch()
                notification.info(t`Successfully updated`)
              } else {
                notification.error(t`Unable to update`)
              }
            }}
          />
          {` `}
          <Trans>Receive activity summary emails</Trans>
          {` `}
          <Tooltip
            text={
              <Trans>
                Stay in the looop with an email update (no more than once daily)
                if there is activity relevant to you.
              </Trans>
            }
          >
            <IconQuestion />
          </Tooltip>
        </div>
      )}

      <div className="User__statusInfo">
        {isAdminEditing && <UserIsActiveSwitch />}
        {isAdminEditing && <UserCreationInfo userId={user.id} />}
      </div>

      {isAdminEditing && (
        <UserPasswordReset userId={user.id} userEmail={user.email} />
      )}

      {isAdminEditing && (
        <div className="User__archive">
          {data?.me.isOrganisationOwner &&
            data.me.organisation_role === OrganisationRoleEnum.ADMIN && (
              <LogInAsUser userId={user.id} />
            )}
          <h3>
            <Trans>Archive user account</Trans>
          </h3>
          <Message type={MessageTypes.WARNING}>
            <ButtonDanger
              disabled={loading || !!user.scimResource}
              isLoading={loading}
              onClick={() => setShowArchiveModal(true)}
            >
              <Trans>Archive user</Trans>
            </ButtonDanger>
            {user.scimResource ? (
              <Trans>SCIM users cannot be archived from Looop</Trans>
            ) : (
              <Trans>This action cannot be undone</Trans>
            )}
          </Message>
          <Modal
            show={showArchiveModal}
            onHide={() => setShowArchiveModal(false)}
            controls={
              <>
                <ButtonClean onClick={() => setShowArchiveModal(false)}>
                  <Trans>Cancel</Trans>
                </ButtonClean>
                <ButtonDanger
                  disabled={loading}
                  isLoading={loading}
                  onClick={async () => {
                    const { data } = await archiveUser({
                      variables: {
                        id: user.id
                      }
                    })
                    if (data?.admin?.user?.archive) {
                      navigateTo(staticLinks.users)
                      notification.info(t`User successfully archived`)
                    } else {
                      notification.error(t`Unable to archive user`)
                    }
                  }}
                >
                  <Trans>Archive user</Trans>
                </ButtonDanger>
              </>
            }
            header={<Trans>Just checking</Trans>}
          >
            <Trans>Are you sure you want to archive this user?</Trans>
          </Modal>
        </div>
      )}
    </div>
  )
}

export default UserBasicInfo
